import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getShippingAddresses,
    setMainShippingAddress,
    deleteShippingAddress
} from '../../Reducers/actions/shippingAddressActions';
import Icon from '../Icon/Icon';
import ModalMessage from '../ModalMessage/ModalMessage';
import AddShipping from '../AddShipping/AddShipping';
import './ShippingAddressSelector.scss';

const ShippingAddressSelector = ({ onSelect, onClose, selectedAddressId }) => {
    const dispatch = useDispatch();
    const { addresses, loading } = useSelector((state) => state.shippingAddress || { addresses: [], loading: false });
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);

    useEffect(() => {
        dispatch(getShippingAddresses());
    }, [dispatch]);

    const handleSelectAddress = (address) => {
        if (onSelect) {
            onSelect(address);
        }
        if (onClose) {
            onClose();
        }
    };

    const handleSetMain = async (addressId, e) => {
        e.stopPropagation();
        try {
            await dispatch(setMainShippingAddress(addressId));
            // Get updated addresses and select the main one
            const result = await dispatch(getShippingAddresses());
            const updatedAddresses = result?.response?.data || addresses;
            const mainAddress = updatedAddresses.find(addr => addr.address_id === addressId) || 
                               updatedAddresses.find(addr => addr.is_main);
            if (mainAddress) {
                handleSelectAddress(mainAddress);
            }
        } catch (error) {
            console.error('Error setting main address:', error);
        }
    };

    const handleEdit = (address, e) => {
        e.stopPropagation();
        setEditingAddress(address);
        setShowAddForm(true);
    };

    const handleDelete = (address, e) => {
        e.stopPropagation();
        setAddressToDelete(address);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!addressToDelete) return;
        try {
            const result = await dispatch(deleteShippingAddress(addressToDelete.address_id));
            if (result?.success) {
                setShowDeleteModal(false);
                setAddressToDelete(null);
                dispatch(getShippingAddresses());
            }
        } catch (error) {
            console.error('Error deleting address:', error);
        }
    };

    const handleAddSuccess = () => {
        setShowAddForm(false);
        setEditingAddress(null);
        dispatch(getShippingAddresses());
    };

    // Show add/edit form
    if (showAddForm) {
        return (
            <ModalMessage onClose={() => {
                setShowAddForm(false);
                setEditingAddress(null);
            }}>
                <AddShipping
                    editingAddress={editingAddress}
                    setEditingAddress={setEditingAddress}
                    handleDelete={handleDelete}
                    onSuccess={() => {
                        setShowAddForm(false);
                        setEditingAddress(null);
                        dispatch(getShippingAddresses());
                    }}
                />
            </ModalMessage>
        );
    }

    // Show delete confirmation
    if (showDeleteModal) {
        return (
            <ModalMessage onClose={() => setShowDeleteModal(false)}>
                <div className="delete-confirmation d-flex f-column aic jcc">
                    <p className="fs-sm2 fw-bold mb-32 mt-32">
                        Are you sure you want to delete this address?
                    </p>
                    <button
                        className="btn red-outline-btn btn-full aic jcc fw-extra-bold mb-24"
                        style={{ height: '64px' }}
                        onClick={confirmDelete}
                    >
                        YES, DELETE
                    </button>
                    <p
                        className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        CANCEL
                    </p>
                </div>
            </ModalMessage>
        );
    }

    // Show empty state
    if (!addresses || addresses.length === 0) {
        return (
            <ModalMessage onClose={onClose}>
                <div className="shipping-address-selector">
                    <p className="fs-sm2 fw-bold fg-dark ta-center mb-24">Add Shipping Address</p>
                    <AddShipping
                        editingAddress={null}
                        setEditingAddress={setEditingAddress}
                        handleDelete={handleDelete}
                        onSuccess={() => {
                            setShowAddForm(false);
                            dispatch(getShippingAddresses());
                        }}
                    />
                </div>
            </ModalMessage>
        );
    }

    // Show address list
    return (
        <ModalMessage onClose={onClose}>
            <div className="shipping-address-selector">
                <div className="shipping-address-selector-header">
                    <p className="fs-sm2 fw-bold fg-dark">Select Shipping Address</p>
                    <Icon
                        alias="plus_v2"
                        width={24}
                        height={24}
                        stroke="#FF3232"
                        className="c-pointer"
                        onClick={() => {
                            setEditingAddress(null);
                            setShowAddForm(true);
                        }}
                    />
                </div>
                <div className="shipping-address-selector-list">
                    {addresses.map((address) => (
                        <div
                            key={address.address_id}
                            className={`shipping-address-selector-card ${address.is_main ? 'main-address' : ''} ${selectedAddressId === address.address_id ? 'selected' : ''} c-pointer`}
                            onClick={() => handleSelectAddress(address)}
                        >
                            <div className="address-content">
                                <p className={`address-label ${address.is_main ? 'fg-white' : 'fg-dark'} fw-bold mb-8`}>
                                    {address.is_main ? 'Main address' : 'Other address'}
                                </p>
                                <p className={`address-text ${address.is_main ? 'fg-white' : 'fg-dark'} fs-xs`}>
                                    {address.address_line_1}
                                    {address.address_line_2 && `, ${address.address_line_2}`}
                                    {address.city && `, ${address.city}`}
                                    {address.postal_code && ` ${address.postal_code}`}
                                    {address.country && `, ${address.country}`}
                                </p>
                            </div>
                            <div className="address-actions">
                                {!address.is_main && (
                                    <Icon
                                        alias="check-circle"
                                        width={20}
                                        height={20}
                                        color={selectedAddressId === address.address_id ? '#FF3232' : 'transparent'}
                                        className="c-pointer mr-8"
                                        onClick={(e) => handleSetMain(address.address_id, e)}
                                    />
                                )}
                                <Icon
                                    alias="edit_shipping_address"
                                    width={20}
                                    height={20}
                                    color={address.is_main ? '#FFF' : '#FF3232'}
                                    stroke={address.is_main ? '#FFF' : '#FF3232'}
                                    strokeWidth={2}
                                    className="c-pointer"
                                    onClick={(e) => handleEdit(address, e)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </ModalMessage>
    );
};

export default ShippingAddressSelector;

